/**
 * Generic control renderer component that handles rendering different control types
 * based on the slot configuration from layout strategies.
 */

import {includes} from 'lodash';
import React from 'react';

import {keyDisplayName} from '../../../../util/runs';
import {MediaBrowserPanelProps, MediaConfigWithDefaults} from '../../Component';
import {AxisRangeControl, IndexSlider, StepSlider} from '../../controls';
import {StepMeta} from '../../types';
import {ControlDefinition, GridData} from '../types';
import {getActiveAxes, useStepSliderKeyboardBindings} from '../utils/slotUtils';

export type ControlRendererProps = {
  control: ControlDefinition;
  index: number;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  currentStep: number;
  mediaIndex: number;
  additionalProps?: {
    isMaxStepLoading?: boolean;
    mediaBrowserSize?: {width: number; height: number};
    gridData?: GridData;
    columnCount?: number;
  };
};

/**
 * Renders a single control based on its type and configuration.
 *
 * @param props - Control renderer props
 * @returns Rendered control or null if hidden/invalid
 */
export const ControlRenderer: React.FC<ControlRendererProps> = ({
  control,
  index,
  config,
  updateConfig,
  stepMeta,
  currentStep,
  mediaIndex,
  additionalProps = {},
}) => {
  const stepSliderKeyboardBindings = useStepSliderKeyboardBindings();
  const activeAxes = getActiveAxes(config);

  // 'label' type controls are rendered as static text in header/footer slots, not here
  if (control.type === 'label') {
    return null;
  }

  switch (control.control) {
    case 'step': {
      const isStepOnAxis = includes(activeAxes, 'step');
      if (isStepOnAxis) {
        return null; // Step is on an axis, so don't show slider
      }

      const minStep = stepMeta?.minSliderStep ?? stepMeta.min;
      const maxStep = stepMeta?.maxSliderStep ?? stepMeta.max;

      if (maxStep <= 0 || minStep >= maxStep) {
        return null; // Invalid range
      }

      return (
        <StepSlider
          key={`step-${index}`}
          min={minStep}
          max={maxStep}
          current={currentStep}
          steps={stepMeta.sliderSteps ?? stepMeta.steps}
          sliderKey={stepMeta.sliderKey}
          label={keyDisplayName({
            section: 'summary',
            name: stepMeta.sliderKey,
          })}
          snapToExistingStep={config.snapToExistingStep ?? true}
          strideLength={config.stepStrideLength ?? 1}
          keyboardBindings={stepSliderKeyboardBindings}
          onChange={value =>
            updateConfig({
              stepIndex: value,
            })
          }
          updateConfig={updateConfig}
        />
      );
    }

    case 'index': {
      const isIndexOnAxis = includes(activeAxes, 'index');
      if (isIndexOnAxis) {
        return null; // Index is on an axis, so don't show slider
      }

      const minIndex = stepMeta.minIndex;
      const maxIndex = stepMeta.maxIndex;

      if (maxIndex <= 0) {
        return null; // No media to show
      }

      return (
        <IndexSlider
          key={`index-${index}`}
          min={minIndex}
          max={maxIndex}
          current={mediaIndex}
          label="Index"
          onChange={value => updateConfig({mediaIndex: value})}
        />
      );
    }

    case 'xAxisRange': {
      const {isMaxStepLoading, mediaBrowserSize, gridData, columnCount} =
        additionalProps;

      // Only show if we have the required data
      const showXAxis =
        config.gridSettings?.xAxis === 'step' &&
        gridData?.metadata.xAxisTicks != null &&
        gridData?.metadata.allXAxisValues != null &&
        (config.columnCount ?? 1) > 1 &&
        !isMaxStepLoading;

      if (!showXAxis || !gridData || !mediaBrowserSize || !columnCount) {
        return null;
      }

      return (
        <AxisRangeControl
          key={`xAxisRange-${index}`}
          axis="xAxis"
          axisLabel="Steps"
          gridData={gridData}
          config={config}
          width={mediaBrowserSize.width}
          columnCount={columnCount}
          updateConfig={updateConfig}
          direction="bottom"
        />
      );
    }

    case 'yAxisRange': {
      const {isMaxStepLoading, mediaBrowserSize, gridData} = additionalProps;

      // Only show if we have the required data
      const showYAxis =
        config.gridSettings?.yAxis != null &&
        gridData?.metadata.yAxisTicks != null &&
        gridData?.metadata.allYAxisValues != null &&
        !isMaxStepLoading;

      if (!showYAxis || !gridData || !mediaBrowserSize) {
        return null;
      }

      return (
        <AxisRangeControl
          key={`yAxisRange-${index}`}
          axis="yAxis"
          axisLabel={config.gridSettings?.yAxis === 'step' ? 'Steps' : 'Runs'}
          gridData={gridData}
          config={config}
          width={mediaBrowserSize.width}
          updateConfig={updateConfig}
          direction="left"
        />
      );
    }

    case 'run':
      return <div key={`run-${index}`}></div>;

    default:
      return null;
  }
};
